import { useEffect, useState, type FormEvent } from 'react';
import { isApiError } from '../../lib/api';
import {
  checkShopify,
  connectShopify,
  disconnectShopify,
  getShopifyConnection,
  resetShopifyStatus,
  saveShopifyAi,
  type ShopifyConnectionView,
} from '../../lib/shopify-api';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';
import { SecretInput } from './SecretInput';

// Admin → Site → Shopify. Connects one Shopify store to the site so agents see
// the customer, their orders, their live cart and the catalogue in a
// conversation's Shop tab. Read-only: nothing here can change the store.

type Method = 'token' | 'client_credentials';

export function ShopifySection({ siteId }: { siteId: string }) {
  const [conn, setConn] = useState<ShopifyConnectionView | null>(null);
  const [scopes, setScopes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [method, setMethod] = useState<Method>('token');
  const [shopDomain, setShopDomain] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [storefrontToken, setStorefrontToken] = useState('');
  const [aiEnabled, setAiEnabled] = useState(false);
  const [allowedActions, setAllowedActions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await getShopifyConnection(siteId);
      setConn(r.connection);
      setAiEnabled(r.connection?.aiEnabled ?? false);
      setAllowedActions(r.connection?.aiAllowedActions ?? []);
      setStorefrontToken('');
      setScopes(r.requiredScopes);
      setError(null);
    } catch {
      setError('Could not load the Shopify connection.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  function explain(err: unknown, fallback: string): string {
    if (isApiError(err) && err.body && typeof err.body === 'object') {
      const b = err.body as { error?: string; detail?: string };
      if (b.error === 'encryption-unavailable') return 'Set CREDENTIALS_ENC_KEY on the server first: store credentials are kept encrypted.';
      if (b.detail) return b.detail;
    }
    return fallback;
  }

  async function connect(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await connectShopify(
        siteId,
        method === 'token'
          ? { shopDomain: shopDomain.trim(), accessToken: accessToken.trim() }
          : { shopDomain: shopDomain.trim(), clientId: clientId.trim(), clientSecret: clientSecret.trim() },
      );
      setConn(r.connection);
      setAiEnabled(r.connection.aiEnabled);
      setAllowedActions(r.connection.aiAllowedActions);
      setStorefrontToken('');
      setEditing(false);
      setAccessToken(''); setClientSecret(''); setClientId('');
      resetShopifyStatus(siteId);
      setNotice(`Connected to ${r.connection.shopName ?? r.connection.shopDomain}. Agents now see a Shop tab in this site’s conversations.`);
    } catch (err) {
      setError(explain(err, 'Could not connect the store.'));
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await checkShopify(siteId);
      setConn(r.connection);
      if (r.result.ok) setNotice('Connection OK.');
      else setError(r.result.detail ?? 'Check failed.');
    } catch (err) {
      setError(explain(err, 'Check failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!(await confirmDialog({ title: 'Disconnect the Shopify store?', message: 'Agents lose the Shop tab and the stored credentials are deleted.', confirmLabel: 'Disconnect' }))) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await disconnectShopify(siteId);
      resetShopifyStatus(siteId);
      setConn(null);
      setAiEnabled(false);
      setStorefrontToken('');
      setNotice('Store disconnected.');
    } catch (err) {
      setError(explain(err, 'Could not disconnect.'));
    } finally {
      setBusy(false);
    }
  }

  async function saveAi(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await saveShopifyAi(siteId, {
        enabled: aiEnabled,
        allowedActions,
        ...(storefrontToken.trim() ? { storefrontToken: storefrontToken.trim() } : {}),
      });
      setConn(r.connection);
      setAiEnabled(r.connection.aiEnabled);
      setAllowedActions(r.connection.aiAllowedActions);
      setStorefrontToken('');
      setNotice(r.connection.aiEnabled ? 'Shopify AI actions enabled. Product and order access verified.' : 'Shopify AI actions disabled. The agent Shop tab remains available.');
    } catch (err) {
      setError(explain(err, 'Could not save Shopify AI settings.'));
    } finally {
      setBusy(false);
    }
  }

  const showForm = !conn || editing;

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Shopify</p>
      <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)', margin: 0 }}>
        Connect this site’s Shopify store and agents get a <strong>Shop</strong> tab in every conversation: the customer’s
        orders, spend and tracking, the cart they have open on the store, and product and order lookup. Access is
        read-only: Openline never changes the store.
      </p>
      {error && <p className={styles.error} role="alert">{error}</p>}
      {notice && <p className={styles.notice} role="status">{notice}</p>}

      {loading ? (
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>Loading…</p>
      ) : conn ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <tbody>
              <tr><th>Store</th><td>{conn.shopName ?? '—'} <span className={styles.mono}>{conn.shopDomain}</span></td></tr>
              <tr><th>Status</th><td>
                <span className={conn.status === 'active' ? styles.badgeAccent : styles.badge}>{conn.status === 'active' ? 'connected' : 'error'}</span>
                {conn.lastError ? <span style={{ marginLeft: 8 }}>{conn.lastError}</span> : null}
              </td></tr>
              <tr><th>Currency</th><td>{conn.currency ?? '—'}</td></tr>
              <tr><th>Credentials</th><td>{conn.authMode === 'token' ? 'Admin API access token' : 'Client ID + secret (Dev Dashboard app)'}</td></tr>
              <tr><th>Last checked</th><td>{conn.lastCheckedAt ? new Date(conn.lastCheckedAt).toLocaleString() : '—'}</td></tr>
            </tbody>
          </table>
        </div>
      ) : null}

      {conn && !editing ? (
        <div className={styles.formActions} style={{ marginTop: 'var(--inic-space-3)' }}>
          <button type="button" className={styles.buttonGhost} disabled={busy} onClick={() => void check()}>Check</button>
          <button type="button" className={styles.buttonGhost} disabled={busy} onClick={() => { setEditing(true); setShopDomain(conn.shopDomain); setMethod(conn.authMode); }}>Replace credentials</button>
          <a className={styles.buttonGhost} href={conn.adminUrl} target="_blank" rel="noreferrer noopener">Open store admin</a>
          <button type="button" className={styles.buttonDanger} disabled={busy} onClick={() => void disconnect()}>Disconnect</button>
        </div>
      ) : null}

      {showForm && !loading ? (
        <form className={styles.form} onSubmit={(e) => void connect(e)} style={{ marginTop: 'var(--inic-space-3)' }}>
          <label className={styles.field}>
            <span className={styles.label}>Store domain</span>
            <input className={styles.input} required value={shopDomain} onChange={(e) => setShopDomain(e.target.value)} placeholder="acme.myshopify.com" />
            <span className={styles.hint}>The myshopify.com domain, not the store’s custom domain.</span>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Credentials</span>
            <select className={styles.select} value={method} onChange={(e) => setMethod(e.target.value as Method)}>
              <option value="token">Admin API access token</option>
              <option value="client_credentials">Client ID + secret</option>
            </select>
          </label>
          {method === 'token' ? (
            <label className={styles.field}>
              <span className={styles.label}>Admin API access token</span>
              <SecretInput required value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder="shpat_…" />
            </label>
          ) : (
            <>
              <label className={styles.field}>
                <span className={styles.label}>Client ID</span>
                <input className={styles.input} required autoComplete="off" value={clientId} onChange={(e) => setClientId(e.target.value)} />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Client secret</span>
                <SecretInput required value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
              </label>
            </>
          )}
          <div className={styles.formActions}>
            <button type="submit" className={styles.button} disabled={busy}>{busy ? 'Testing…' : conn ? 'Save and test →' : 'Connect store →'}</button>
            {editing ? <button type="button" className={styles.buttonGhost} onClick={() => setEditing(false)}>Cancel</button> : null}
          </div>
        </form>
      ) : null}

      {conn && !editing ? (
        <form className={styles.form} onSubmit={(e) => void saveAi(e)} style={{ marginTop: 'var(--inic-space-4)' }}>
          <p className={styles.kicker} style={{ gridColumn: '1 / -1' }}>Shopify AI actions</p>
          <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
            <span className={styles.label}>Storefront API access token</span>
            <SecretInput
              required={aiEnabled && !conn.hasStorefrontToken} disabled={busy}
              value={storefrontToken} onChange={(e) => setStorefrontToken(e.target.value)}
              placeholder={conn.hasStorefrontToken ? 'Saved — leave empty to keep' : ''}
            />
            <span className={styles.hint}>For AI product search and related recommendations. Products must be published to this token’s sales channel. Order checks use the store’s saved Admin credentials.</span>
          </label>
          <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
            <span><input type="checkbox" checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} disabled={busy} /> Enable Shopify AI actions in visitor chat</span>
          </label>
          <fieldset className={styles.field} style={{ gridColumn: '1 / -1' }}>
            <legend className={styles.label}>Allowed AI actions</legend>
            {([['products', 'Product lookup'], ['recommendations', 'Recommendations'], ['order_status', 'Order status']] as const).map(([action, label]) => (
              <label key={action}><input type="checkbox" checked={allowedActions.includes(action)} disabled={busy} onChange={(e) => setAllowedActions((current) => e.target.checked ? [...current, action] : current.filter((a) => a !== action))} /> {label}</label>
            ))}
            <span className={styles.hint}>Only selected actions can run. Attempts and results appear in Admin → Audit log.</span>
          </fieldset>
          <p className={styles.hint} style={{ gridColumn: '1 / -1' }}>Order status is shared only after the visitor supplies an order number and checkout email that both match the order. The assistant receives status fields only.</p>
          <div className={styles.formActions}>
            <button type="submit" className={styles.button} disabled={busy}>Save AI settings</button>
          </div>
        </form>
      ) : null}

      <details style={{ marginTop: 'var(--inic-space-3)' }}>
        <summary className={styles.label} style={{ cursor: 'pointer' }}>How to create the Shopify app</summary>
        <ol style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)', paddingLeft: 'var(--inic-space-4)' }}>
          <li>
            Create an app for the store: in the Shopify admin (Settings → Apps → Develop apps), or in the Shopify Dev
            Dashboard for newer stores.
          </li>
          <li>
            Give it these Admin API access scopes, all read-only: <code className={styles.mono}>{scopes.join(', ')}</code>.
            Add <code className={styles.mono}>read_all_orders</code> to see orders older than 60 days.
          </li>
          <li>Under protected customer data, allow access to customer name, email, phone and address.</li>
          <li>
            Install the app on the store, then paste here either the <strong>Admin API access token</strong> it shows
            (starts with <code className={styles.mono}>shpat_</code>), or the app’s <strong>client ID and secret</strong>.
          </li>
        </ol>
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>
          The live cart needs nothing extra: the chat widget on the storefront reads it from the store’s own cart. The
          credentials are tested before they are saved, stored encrypted, and never shown again.
        </p>
      </details>
    </section>
  );
}
